import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { AviaPassengerFareFragment, Money as MoneyType } from '@websky/graphql';
import { useTheme } from '../../../../../../../theme';
import { useShowTaxesInfo } from '../../../../../../../hooks';
import Button from '../../../../../../../BaseComponents/Button';
import Money from '../../../../../../../Money/components/Money';
import PriceInMiles from '../../../../../../../PriceInMiles/PriceInMiles';
import { TaxesCollapse } from '../../../TaxesCollapse';
import { buildFareBreakdown } from '../../../../model/fareBreakdown';
import { CdnIcon } from '@shared/ui';

const LONG_PRICE_DIGITS_THRESHOLD = 8;

interface Props {
	priceInMiles?: number;
	subscribed?: boolean;
	onClick: React.MouseEventHandler<HTMLButtonElement>;
	onTouchEndCapture?: React.TouchEventHandler<HTMLButtonElement>;
	disabled?: boolean;
	price: MoneyType;
	originalPrice?: MoneyType;
	passengerFares?: AviaPassengerFareFragment[];
	selected?: boolean;
	className?: string;
}

const FareButton: React.FC<Props> = ({
	priceInMiles,
	price,
	subscribed,
	originalPrice,
	passengerFares,
	onClick,
	disabled,
	selected,
	className,
	onTouchEndCapture
}) => {
	const { FareButton: css } = useTheme('Entities').FareGroupV2;
	const { t } = useTranslation('FareGroup');

	const showTaxesInfo = useShowTaxesInfo();

	const breakdown = useMemo(() => buildFareBreakdown({ passengerFares: passengerFares ?? [] }), [passengerFares]);

	const showOriginalPrice = originalPrice && originalPrice.amount !== price.amount;

	const isLongPrice = Math.floor(Math.abs(price?.amount)).toString().length >= LONG_PRICE_DIGITS_THRESHOLD;

	const showSelectTitle = !showOriginalPrice && !isLongPrice;

	const isSelected = selected && !disabled;
	const isSubscribed = subscribed && !isSelected;
	const fareButtonState = disabled ? 'unavailable' : isSubscribed ? 'subscribed' : isSelected ? 'selected' : 'select';

	const btnClassName = cn(css.root, className, {
		[css.selected]: isSelected,
		[css.subscribed]: isSubscribed
	});

	const buttonContent = (
		<>
			{!disabled ? (
				<>
					{showSelectTitle && <span className={css.title}>{t(isSelected ? 'Selected' : 'Select')}</span>}

					{priceInMiles ? (
						<PriceInMiles
							amount={priceInMiles}
							amountClassName={css.miles_amount}
							moneyClassName={css.miles}
						/>
					) : (
						<div className={cn(css.money_wrapper, { [css.money_wrapper_center]: showOriginalPrice })}>
							{showOriginalPrice && (
								<Money moneyClassName={css.money_original} strikethrough money={originalPrice} />
							)}
							<Money moneyClassName={css.money} money={price} />
						</div>
					)}
				</>
			) : (
				<>{t('Unavailable')}</>
			)}
		</>
	);

	if (showTaxesInfo && isSelected && breakdown.groups.length) {
		return (
			<TaxesCollapse
				className={btnClassName}
				breakdown={{ ...breakdown, total: price }}
				dataTestId="fare-select-button"
				dataState={fareButtonState}
			>
				{isSelected && <CdnIcon icon="Info/Choosed" />}
				{buttonContent}
				{disabled && <CdnIcon icon="Info/Not included" />}
			</TaxesCollapse>
		);
	}

	return (
		<Button
			classes={{
				root: btnClassName,
				disabled: css.disabled,
				endIcon: css.endIcon,
				startIcon: css.startIcon
			}}
			disabled={disabled}
			onClick={onClick}
			startIcon={isSelected && <CdnIcon icon="Info/Choosed" />}
			endIcon={disabled && <CdnIcon icon="Info/Not included" />}
			onTouchEndCapture={onTouchEndCapture}
			disableRipple={isSelected}
			data-test-id="fare-select-button"
			data-state={fareButtonState}
		>
			{buttonContent}
		</Button>
	);
};

export default FareButton;
